import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { useInView } from 'react-intersection-observer';
import { default as awardsBg } from '../assets/awards_bg.png';
import image1 from '../assets/image1.JPG';
import image2 from '../assets/image2.JPG';
import image3 from '../assets/image3.JPG';
import { logCustomEvent } from '../firebase';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  IconButton,
} from '@mui/material';
import {
  Trophy,
  Eye,
  ChevronDown,
  Users,
  Lightbulb,
  TrendingUp,
  Award,
  ArrowRight,
  Play,
  Star,
  UserPlus,
  FileText,
  Search,
  Gavel,
  PartyPopper,
  Microscope,
  Briefcase,
  Heart,
  MapPin,
  Calendar,
  Clock,
} from 'lucide-react';
import theme from '../theme';
import { default as award6 } from '../assets/sacred.png';
import pageContent from '../data/pageContent.json';


const Home = () => {
  const { lang } = useParams();
  const { t, i18n } = useTranslation('home');
  const currentLang = lang || i18n.language || 'en';

  // Keep pageContent for data that's not in translation files (images, videos, etc.)
  const { sponsors: sponsorsPageContent, winnersGallery: winnersPageContent, about } = pageContent.home;

  // Get translated sponsor names and titles, then merge with video URLs from pageContent
  const sponsors = {
    ...sponsorsPageContent,
    list: (t('sponsors.list', { returnObjects: true }) || []).map((sponsor, index) => ({
      name: sponsor.name,
      title: sponsor.title,
      logo: sponsorsPageContent.list[index]?.logo,
      video: sponsorsPageContent.list[index]?.video
    }))
  };

  // Get translated winner names, locations, and categories, then merge with video URLs and year from pageContent
  const winnersGallery = {
    ...winnersPageContent,
    winners: (t('winnersGallery.winners', { returnObjects: true }) || []).map((winner, index) => ({
      name: winner.name,
      org: winner.org,
      category: winner.category,
      year: winnersPageContent.winners[index]?.year,
      videoUrl: winnersPageContent.winners[index]?.videoUrl
    }))
  };

  // Gallery image mapping
  const galleryImageMap = {
    '/assets/image1.JPG': image1,
    '/assets/image2.JPG': image2,
    '/assets/image3.JPG': image3,
  };

  // Hero Section Component
  const HeroSection = () => {
    const [timeLeft, setTimeLeft] = useState({
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
    });

    useEffect(() => {
      const ceremonyDate = new Date('2026-02-27');
      // ceremonyDate.setFullYear(new Date().getFullYear());
      
      const timer = setInterval(() => {
        const now = new Date();
        const difference = ceremonyDate - now;
        
        if (difference > 0) {
          setTimeLeft({
            days: Math.floor(difference / (1000 * 60 * 60 * 24)),
            hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
            minutes: Math.floor((difference / 1000 / 60) % 60),
            seconds: Math.floor((difference / 1000) % 60),
          });
        }
      }, 1000);

      return () => clearInterval(timer);
    }, []);

    return (
      <Box
        sx={{
          position: 'relative',
          pt: { xs: "160px", lg: "120px" },
          pb: { xs: 'calc(4rem + 100px)', lg: 6 },
          minHeight: { xs: 'auto', lg: '100vh' },
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${awardsBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        {/* Overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: `
              linear-gradient(180deg, rgba(0, 0, 0, 0.875) 60%, rgba(0, 0, 0, 0.5) 100%)
            `,
          }}
        />
        
        <Box sx={{ position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', width: '100%'}}>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'center'}}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              style={{ textAlign: 'center', color: 'white', maxWidth: '800px', padding: '0 1rem', width: '100%', boxSizing: 'border-box' }}
            >
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.2rem' },
                    fontWeight: 'bold',
                    lineHeight: 1.1,
                    transform: 'perspective(800px) rotateY(0deg)',
                    transformStyle: 'preserve-3d',
                    color: ' #D4A747',
                  }}
                >
                  {t('hero.title')}
                </Typography>

                {/* Mission & Venue Section - Clean Modern Design */}
                <Box
                  sx={{
                    mt: 1.5,
                    mb: 4,
                    position: 'relative',
                    width: '100%',
                    mx: 'auto',
                  }}
                >
                  {/* Simple Mission Subheading */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: '1rem', md: '1.2rem' },
                        fontWeight: 300,
                        textAlign: 'center',
                        color: 'rgba(244, 228, 188, 0.8)',
                        letterSpacing: 0.5,
                        lineHeight: 1.6,
                        mb: { xs: 2, md: 4 },
                      }}
                    >
                      {t('hero.subtitle')}
                    </Typography>
                  </motion.div>

                  {/* Venue & Date - Split Modern Cards */}
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.8,
                      delay: 0.4,
                      ease: [0.43, 0.13, 0.23, 0.96]
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 2,
                        mb: { xs: 3, md: 5 },
                      }}
                    >
                      {/* Venue Card */}
                      <Box
                        sx={{
                          position: 'relative',
                          group: 'venue',
                          p: { xs: 2, md: 3 },
                          background: `
                            linear-gradient(135deg,
                              rgba(212, 167, 71, 0.08) 0%,
                              rgba(26, 26, 26, 0.4) 100%
                            )
                          `,
                          backdropFilter: 'blur(20px)',
                          borderRadius: '16px',
                          border: '1px solid rgba(212, 167, 71, 0.15)',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                          '&:hover': {
                            transform: 'translateY(-3px) scale(1.02)',
                            borderColor: 'rgba(212, 167, 71, 0.3)',
                            '& .venue-icon': {
                              transform: 'rotate(360deg) scale(1.1)',
                            },
                            '& .venue-glow': {
                              opacity: 1,
                            },
                          },
                        }}
                      >
                        {/* Glow Effect */}
                        <Box
                          className="venue-glow"
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '150%',
                            height: '150%',
                            background: 'radial-gradient(circle, rgba(212, 167, 71, 0.1) 0%, transparent 70%)',
                            opacity: 0,
                            transition: 'opacity 0.4s ease',
                            pointerEvents: 'none',
                          }}
                        />

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, position: 'relative' }}>
                          <Box
                            className="venue-icon"
                            sx={{
                              width: { xs: 36, md: 45 },
                              height: { xs: 36, md: 45 },
                              borderRadius: '12px',
                              background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              boxShadow: '0 8px 16px rgba(212, 167, 71, 0.25)',
                              transition: 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
                            }}
                          >
                            <MapPin size={18} style={{ color: '#1A1A1A' }} />
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontSize: '0.65rem',
                                letterSpacing: 2,
                                textTransform: 'uppercase',
                                color: 'rgba(212, 167, 71, 0.7)',
                                fontWeight: 700,
                                mb: 0.5,
                              }}
                            >
                              {t('hero.venue.label')}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: { xs: '0.95rem', md: '1.1rem' },
                                fontWeight: 400,
                                color: 'rgba(255, 255, 255, 0.9)',
                                lineHeight: 1.3,
                              }}
                            >
                              {t('hero.venue.name')}
                              <Box component="span" sx={{ display: 'block', fontSize: '0.85em', opacity: 0.7, mt: 0.3 }}>
                                {t('hero.venue.city')}
                              </Box>
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      {/* Date Card */}
                      <Box
                        sx={{
                          position: 'relative',
                          p: { xs: 2, md: 3 },
                          background: `
                            linear-gradient(135deg,
                              rgba(244, 228, 188, 0.08) 0%,
                              rgba(26, 26, 26, 0.4) 100%
                            )
                          `,
                          backdropFilter: 'blur(20px)',
                          borderRadius: '16px',
                          border: '1px solid rgba(244, 228, 188, 0.15)',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                          '&:hover': {
                            transform: 'translateY(-3px) scale(1.02)',
                            borderColor: 'rgba(244, 228, 188, 0.3)',
                            '& .date-icon': {
                              transform: 'rotate(360deg) scale(1.1)',
                            },
                            '& .date-glow': {
                              opacity: 1,
                            },
                          },
                        }}
                      >
                        {/* Glow Effect */}
                        <Box
                          className="date-glow"
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: '150%',
                            height: '150%',
                            background: 'radial-gradient(circle, rgba(244, 228, 188, 0.1) 0%, transparent 70%)',
                            opacity: 0,
                            transition: 'opacity 0.4s ease',
                            pointerEvents: 'none',
                          }}
                        />

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, position: 'relative' }}>
                          <Box
                            className="date-icon"
                            sx={{
                              width: { xs: 36, md: 45 },
                              height: { xs: 36, md: 45 },
                              borderRadius: '12px',
                              background: 'linear-gradient(135deg, #F4E4BC 0%, #D4A747 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              boxShadow: '0 8px 16px rgba(244, 228, 188, 0.25)',
                              transition: 'transform 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
                            }}
                          >
                            <Calendar size={18} style={{ color: '#1A1A1A' }} />
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontSize: '0.65rem',
                                letterSpacing: 2,
                                textTransform: 'uppercase',
                                color: 'rgba(244, 228, 188, 0.7)',
                                fontWeight: 700,
                                mb: 0.5,
                              }}
                            >
                              {t('hero.eventDate.label')}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: { xs: '0.95rem', md: '1.1rem' },
                                fontWeight: 400,
                                color: 'rgba(255, 255, 255, 0.9)',
                                lineHeight: 1.3,
                              }}
                            >
                              {t('hero.eventDate.date')}
                              <Box component="span" sx={{ display: 'block', fontSize: '0.85em', opacity: 0.7, mt: 0.3 }}>
                                {t('hero.eventDate.time')}
                              </Box>
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </motion.div>
                </Box>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 4, justifyContent: 'center', mb: { xs: 2, md: 6 }, flexWrap: 'wrap', alignItems: 'center', width: '100%', px: { xs: 2, sm: 0 } }}>
                {[
                  { text: t('hero.cta.applyNow'), link: `/${currentLang}/nominations`, icon: 'Trophy', variant: 'contained', external: false },
                  { text: t('hero.cta.knowMore'), link: `/${currentLang}/about`, icon: 'Eye', variant: 'outlined', external: false },
                  { text: t('hero.cta.eventSponsor'), link: `/${currentLang}/sponsor`, icon: 'Star', variant: 'standard', external: false }
                ].map((button, index) => {
                  const IconComponent = button.icon === 'Trophy' ? Trophy : button.icon === 'Eye' ? Eye : Star;
                  return (
                    <Box key={index} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                      <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.98 }}>
                        <Button
                          component={button.external ? "a" : Link}
                          {...(button.external ? { href: button.link, target: "_blank", rel: "noopener noreferrer" } : { to: button.link })}
                          variant={button.variant}
                          size="medium"
                          startIcon={<IconComponent size={18} />}
                          onClick={() => {
                            logCustomEvent('cta_button_clicked', {
                              button_text: button.text,
                              button_link: button.link,
                              location: 'hero_section'
                            });
                          }}
                          sx={{
                            ...(button.variant === 'contained' ? {
                              background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                              color: '#1A1A1A',
                              boxShadow: '0 8px 24px rgba(212, 167, 71, 0.25)',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #B8901C 0%, #9A7A18 100%)',
                                boxShadow: '0 12px 32px rgba(212, 167, 71, 0.4)',
                              },
                            } : button.variant === 'outlined' ? {
                              borderColor: 'rgba(212, 167, 71, 0.5)',
                              color: '#D4A747',
                              border: '1.5px solid rgba(212, 167, 71, 0.5)',
                              '&:hover': {
                                backgroundColor: '#D4A747',
                                color: '#1A1A1A',
                                borderColor: '#D4A747',
                              },
                            } : {
                              background: 'white',
                              color: '#333333',
                              boxShadow: '0 8px 24px rgba(26, 26, 26, 0.2)',
                              '&:hover': {
                                background: '#f8f9fa',
                                boxShadow: '0 12px 32px rgba(26, 26, 26, 0.3)',
                              },
                            }),
                            width: { xs: '100%', sm: 'auto' },
                            px: 4,
                            py: 1,
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: '8px',
                            transition: 'all 0.3s ease',
                          }}
                        >
                          {button.text}
                        </Button>
                      </motion.div>
                    </Box>
                  );
                })}
              </Box>

              {/* Countdown Timer */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, color: '#D4A747', fontSize: { xs: '1rem', md: '1.25rem' } }}>
                  <Clock size={20} />
                  {t('hero.countdownTitle')}
                </Typography>
                <Grid container spacing={2} sx={{ maxWidth: { xs: '100%', sm: 400 }, mx: 'auto' }}>
                  {Object.entries(timeLeft).map(([unit, value]) => (
                    <Grid size={3} key={unit}>
                      <Box
                        sx={{
                          backgroundColor: 'rgba(212, 167, 71, 0.08)',
                          backdropFilter: 'blur(12px)',
                          borderRadius: 2,
                          p: { xs: 1.5, md: 2 },
                          textAlign: 'center',
                          border: '1px solid rgba(212, 167, 71, 0.15)',
                        }}
                      >
                        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#D4A747', fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
                          {value.toString().padStart(2, '0')}
                        </Typography>
                        <Typography variant="caption" sx={{ textTransform: 'uppercase', color: '#F4E4BC', fontSize: { xs: '0.6rem', md: '0.75rem' } }}>
                          {t(`hero.countdownUnits.${unit}`)}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </motion.div>
          </Box>

        </Box>
      </Box>
    );
  };

  // Sponsors Section with Modern Carousel
  const SponsorsSection = () => {
    const [playingVideo, setPlayingVideo] = useState(null);
    const [currentSlide, setCurrentSlide] = useState(0);
    const videoRefs = useRef([]);
    const [isMobile, setIsMobile] = useState(false);

    // Detect screen size
    useEffect(() => {
      const handleResize = () => {
        const wasMobile = isMobile;
        const nowMobile = window.innerWidth < 1024;
        setIsMobile(nowMobile);

        // Reset to first slide when switching between mobile and desktop
        if (wasMobile !== nowMobile) {
          setCurrentSlide(0);
        }
      };

      handleResize(); // Check on mount
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [isMobile]);

    // Map logo paths from JSON to imported assets
    const logoMap = {
      '/assets/sacred.png': award6,
    };

    const sponsorsList = sponsors.list.map(sponsor => ({
      ...sponsor,
      logo: logoMap[sponsor.logo]
    }));

    const handleVideoPlay = (index) => {
      // Pause any currently playing video
      if (playingVideo !== null && playingVideo !== index) {
        videoRefs.current[playingVideo]?.pause();
      }
      setPlayingVideo(index);
    };

    const handleVideoPause = () => {
      setPlayingVideo(null);
    };

    // Calculate total slides based on screen size
    // Mobile (< 1024px): 1 card per slide
    // Desktop (>= 1024px): 2 cards per slide
    const totalSlides = isMobile ? sponsorsList.length : Math.ceil(sponsorsList.length / 2);

    const nextSlide = () => {
      if (playingVideo !== null) {
        videoRefs.current[playingVideo]?.pause();
        setPlayingVideo(null);
      }
      setCurrentSlide((prev) => (prev + 1) % totalSlides);
    };

    const prevSlide = () => {
      if (playingVideo !== null) {
        videoRefs.current[playingVideo]?.pause();
        setPlayingVideo(null);
      }
      setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    };

    const goToSlide = (index) => {
      if (playingVideo !== null) {
        videoRefs.current[playingVideo]?.pause();
        setPlayingVideo(null);
      }
      setCurrentSlide(index);
    };

    return (
      <Box sx={{ py: { xs: 8, md: 10 }, backgroundColor: theme.palette.grey[50] }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="overline"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  letterSpacing: 1.5,
                  fontSize: '0.875rem',
                }}
              >
                {t('sponsors.overline')}
              </Typography>
              <Typography variant="h2" sx={{
                background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 900,
                mb: 2
              }}>
                {t('sponsors.sectionTitle')}
              </Typography>
              <Typography variant="body1" sx={{ color: 'typography.main', maxWidth: 700, mx: 'auto' }}>
                {t('sponsors.subtitle')}
              </Typography>
            </Box>

            {/* Modern Carousel Container */}
            <Box sx={{ position: 'relative', px: { xs: 0, lg: 8 } }}>
              {/* Carousel Wrapper */}
              <Box
                sx={{
                  position: 'relative',
                  overflow: 'hidden',
                  borderRadius: '20px',
                }}
              >
                {/* Slides Container */}
                <Box
                  sx={{
                    display: 'flex',
                    transform: `translateX(-${currentSlide * 100}%)`,
                    transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  {sponsorsList.map((sponsor, index) => (
                    <Box
                      key={index}
                      sx={{
                        minWidth: { xs: '100%', lg: '50%' },
                        px: { xs: 3.5, lg: 2 },
                        boxSizing: 'border-box',
                      }}
                    >
                    <Box
                      sx={{
                        position: 'relative',
                        borderRadius: '10px',
                        overflow: 'hidden',
                        background: '#FFFFFF',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                        height: '100%',
                      }}
                    >
                      {/* Horizontal Layout Container */}
                      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, height: { xs: 'auto', lg: 320 }, minHeight: { xs: 'auto', lg: 320 } }}>

                        {/* Left Side - Video */}
                        <Box
                          sx={{
                            flex: { xs: '0 0 auto', lg: '0 0 45%' },
                            position: 'relative',
                            background: 'linear-gradient(135deg, #1A1A1A 0%, #2B2B2B 100%)',
                            aspectRatio: { xs: '16/9', lg: 'auto' },
                            height: { xs: 'auto', lg: '100%' },
                            width: { xs: '100%', lg: 'auto' },
                          }}
                        >
                          <Box
                            component="video"
                            ref={(el) => (videoRefs.current[index] = el)}
                            src={sponsor.video}
                            controls={playingVideo === index}
                            preload="metadata"
                            onPlay={() => handleVideoPlay(index)}
                            onPause={handleVideoPause}
                            onEnded={handleVideoPause}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: { xs: 'contain', lg: 'cover' },
                              opacity: playingVideo === index ? 1 : 0.7,
                              cursor: playingVideo === index ? 'default' : 'pointer',
                            }}
                          />

                          {/* Video Overlay - Only show when not playing */}
                          {playingVideo !== index && (
                            <Box
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                background: 'linear-gradient(135deg, rgba(26,26,26,0.3) 0%, rgba(43,43,43,0.5) 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                pointerEvents: 'auto',
                              }}
                              onClick={() => {
                                // Pause any currently playing video
                                if (playingVideo !== null && playingVideo !== index) {
                                  videoRefs.current[playingVideo]?.pause();
                                }
                                setPlayingVideo(index);
                                const video = videoRefs.current[index];
                                if (video) {
                                  video.muted = false;
                                  video.play();
                                }
                              }}
                            >
                              <Box
                                sx={{
                                  width: 50,
                                  height: 50,
                                  borderRadius: '50%',
                                  background: theme.palette.secondary.main,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                <Play size={20} color="white" fill={theme.palette.secondary.main} />
                              </Box>
                            </Box>
                          )}
                        </Box>

                        {/* Right Side - Content */}
                        <Box
                          sx={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            p: { xs: 3, lg: 4 },
                            position: 'relative',
                          }}
                        >
                          {/* Logo Section */}
                          <Box sx={{ mb: { xs: 2, lg: 3 }, display: 'flex', alignItems: 'center', gap: { xs: 2, lg: 3 } }}>
                            <Box
                              sx={{
                                width: { xs: 70, lg: 80 },
                                height: { xs: 70, lg: 80 },
                                borderRadius: '50%',
                                background: 'white',
                                border: { xs: '4px solid #D4A747', lg: '5px solid #D4A747' },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                boxShadow: '0 8px 12px rgba(212, 167, 71, 0.25)',
                              }}
                            >
                              <img
                                src={sponsor.logo}
                                alt={sponsor.name}
                                style={{
                                  width: '45px',
                                  height: '45px',
                                  objectFit: 'contain',
                                }}
                              />
                            </Box>
                          </Box>

                          {/* Company Name */}
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: 800,
                              color: 'typography.main',
                              mb: 1,
                              fontSize: { xs: '1.3rem', lg: '1.6rem' },
                              lineHeight: 1.2,
                            }}
                          >
                            {sponsor.name}
                          </Typography>

                          {/* Title */}
                          <Typography
                            variant="body1"
                            sx={{
                              color: 'primary.main',
                              fontWeight: 600,
                              fontSize: { xs: '0.9rem', lg: '0.95rem' },
                              mb: { xs: 2, lg: 2.5 },
                            }}
                          >
                            {sponsor.title}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Elegant Corner Accent */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          right: 0,
                          width: '120px',
                          height: '120px',
                          background: 'radial-gradient(circle at bottom right, rgba(212, 167, 71, 0.06) 0%, transparent 70%)',
                          pointerEvents: 'none',
                        }}
                      />
                    </Box>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Premium Navigation Arrows */}
              <Box
                sx={{
                  position: 'absolute',
                  top: '40%',
                  left: { xs: 8, lg: -40 },
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                }}
              >
                <Box
                  onClick={prevSlide}
                  sx={{
                    width: { xs: 50, md: 50 },
                    height: { xs: 54, md: 50 },
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateX(-8px) scale(1.08)',
                      '& .outer-ring': {
                        transform: 'rotate(-180deg) scale(1.1)',
                        opacity: 1,
                      },
                      '& .inner-circle': {
                        background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                        boxShadow: '0 0 0 8px rgba(212, 167, 71, 0.1), 0 8px 32px rgba(212, 167, 71, 0.4)',
                      },
                      '& .arrow-icon': {
                        color: '#1A1A1A',
                      },
                    },
                  }}
                >
                  {/* Animated Outer Ring */}
                  <Box
                    className="outer-ring"
                    sx={{
                      position: 'absolute',
                      inset: -6,
                      borderRadius: '50%',
                      border: '2px solid',
                      borderColor: 'transparent',
                      borderTopColor: '#D4A747',
                      borderRightColor: '#D4A747',
                      opacity: 0.3,
                      transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  />

                  {/* Inner Circle */}
                  <Box
                    className="inner-circle"
                    sx={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      background: '#FFFFFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(212, 167, 71, 0.2)',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        background: 'conic-gradient(from 180deg at 50% 50%, rgba(212, 167, 71, 0.1) 0deg, transparent 60deg, transparent 300deg, rgba(212, 167, 71, 0.1) 360deg)',
                        opacity: 0.5,
                      },
                    }}
                  >
                    {/* Arrow Icon */}
                    <Box
                      className="arrow-icon"
                      sx={{
                        color: '#2B2B2B',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        zIndex: 1,
                      }}
                    >
                      <ChevronDown size={28} strokeWidth={2.5} style={{ transform: 'rotate(90deg)' }} />
                    </Box>

                    {/* Decorative Dots */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '15%',
                        width: 3,
                        height: 3,
                        borderRadius: '50%',
                        background: 'rgba(212, 167, 71, 0.4)',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        right: '15%',
                        width: 3,
                        height: 3,
                        borderRadius: '50%',
                        background: 'rgba(212, 167, 71, 0.4)',
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              <Box
                sx={{
                  position: 'absolute',
                  top: '40%',
                  right: { xs: 8, lg: -40 },
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                }}
              >
                <Box
                  onClick={nextSlide}
                  sx={{
                    width: { xs: 50, md: 50 },
                    height: { xs: 50, md: 50 },
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateX(8px) scale(1.08)',
                      '& .outer-ring': {
                        transform: 'rotate(180deg) scale(1.1)',
                        opacity: 1,
                      },
                      '& .inner-circle': {
                        background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                        boxShadow: '0 0 0 8px rgba(212, 167, 71, 0.1), 0 8px 32px rgba(212, 167, 71, 0.4)',
                      },
                      '& .arrow-icon': {
                        color: '#1A1A1A',
                      },
                    },
                  }}
                >
                  {/* Animated Outer Ring */}
                  <Box
                    className="outer-ring"
                    sx={{
                      position: 'absolute',
                      inset: -6,
                      borderRadius: '50%',
                      border: '2px solid',
                      borderColor: 'transparent',
                      borderTopColor: '#D4A747',
                      borderRightColor: '#D4A747',
                      opacity: 0.3,
                      transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                    }}
                  />

                  {/* Inner Circle */}
                  <Box
                    className="inner-circle"
                    sx={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      background: '#FFFFFF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(212, 167, 71, 0.2)',
                      position: 'relative',
                      overflow: 'hidden',
                      transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        background: 'conic-gradient(from 180deg at 50% 50%, rgba(212, 167, 71, 0.1) 0deg, transparent 60deg, transparent 300deg, rgba(212, 167, 71, 0.1) 360deg)',
                        opacity: 0.5,
                      },
                    }}
                  >
                    {/* Arrow Icon */}
                    <Box
                      className="arrow-icon"
                      sx={{
                        color: '#2B2B2B',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        zIndex: 1,
                      }}
                    >
                      <ChevronDown size={28} strokeWidth={2.5} style={{ transform: 'rotate(-90deg)' }} />
                    </Box>

                    {/* Decorative Dots */}
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '15%',
                        width: 3,
                        height: 3,
                        borderRadius: '50%',
                        background: 'rgba(212, 167, 71, 0.4)',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        right: '15%',
                        width: 3,
                        height: 3,
                        borderRadius: '50%',
                        background: 'rgba(212, 167, 71, 0.4)',
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Modern Dot Indicators */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 1.5,
                  mt: 5,
                }}
              >
                {Array.from({ length: totalSlides }).map((_, index) => (
                  <Box
                    key={index}
                    onClick={() => goToSlide(index)}
                    sx={{
                      width: currentSlide === index ? 48 : 12,
                      height: 12,
                      borderRadius: '6px',
                      background: currentSlide === index
                        ? 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)'
                        : '#E0E0E0',
                      cursor: 'pointer',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: currentSlide === index ? '0 4px 12px rgba(212, 167, 71, 0.3)' : 'none',
                      '&:hover': {
                        background: currentSlide === index
                          ? 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)'
                          : '#BDBDBD',
                        transform: 'scale(1.1)',
                      },
                    }}
                  />
                ))}
              </Box>

              {/* Slide Counter */}
              <Box
                sx={{
                  textAlign: 'center',
                  mt: 3,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: 'typography.secondary',
                    fontWeight: 600,
                    fontSize: '0.9rem',
                  }}
                >
                  {currentSlide + 1} / {totalSlides}
                </Typography>
              </Box>
            </Box>
          </motion.div>
        </Container>
      </Box>
    );
  };

  // About Section
  const AboutSection = () => {
    const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.3 });
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const videoRef = useRef(null);

    const handlePlayVideo = () => {
      if (videoRef.current) {
        if (isVideoPlaying) {
          videoRef.current.pause();
        } else {
          videoRef.current.play();
        }
        setIsVideoPlaying(!isVideoPlaying);
      }
    };

    const handleVideoPlay = () => {
      setIsVideoPlaying(true);
    };

    const handleVideoPause = () => {
      setIsVideoPlaying(false);
    };

    const handleVideoEnded = () => {
      setIsVideoPlaying(false);
    };

    // Icon mapping
    const iconMap = {
      Trophy,
      Lightbulb,
      Users
    };

    const keyPoints = [
      { title: t('about.keyPoints.recognition.title'), description: t('about.keyPoints.recognition.description'), icon: iconMap['Trophy'] },
      { title: t('about.keyPoints.innovation.title'), description: t('about.keyPoints.innovation.description'), icon: iconMap['Lightbulb'] },
      { title: t('about.keyPoints.community.title'), description: t('about.keyPoints.community.description'), icon: iconMap['Users'] }
    ];

    const statistics = [
      { value: t('about.statistics.categories.value'), suffix: t('about.statistics.categories.suffix'), label: t('about.statistics.categories.label') },
      { value: t('about.statistics.awards.value'), suffix: t('about.statistics.awards.suffix'), label: t('about.statistics.awards.label') },
      { value: t('about.statistics.farmersImpacted.value'), suffix: t('about.statistics.farmersImpacted.suffix'), label: t('about.statistics.farmersImpacted.label') }
    ];

    return (
      <Box sx={{ pt: { xs: 6, md: 8 }, pb: { xs: 8, md: 12 }, backgroundColor: '#FFFFFF' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                letterSpacing: 1.5,
                fontSize: '0.875rem',
              }}
            >
              {t('about.overline')}
            </Typography>
            <Typography variant="h2" sx={{
              background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 900
            }}>
              {t('about.title')}
            </Typography>
          </Box>

          <Grid container spacing={6} alignItems="flex-end">
            <Grid size={{ xs: 12, lg: 6 }}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <Typography variant="body1" sx={{ color: 'typography.main', mb: 4, lineHeight: 1.7 }}>
                  {t('about.description')}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  {keyPoints.map((point, index) => {
                    const Icon = point.icon;
                    return (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                        viewport={{ once: true }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5 }}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              mr: 2,
                              flexShrink: 0,
                            }}
                          >
                            <Icon size={24} />
                          </Box>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {point.title}
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'typography.main' }}>
                              {point.description}
                            </Typography>
                          </Box>
                        </Box>
                      </motion.div>
                    );
                  })}
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    color: '#1A1A1A',
                  }}
                >
                  {t('about.statistics.heading')}
                </Typography>

                <Grid container spacing={2} ref={ref}>
                  {statistics.map((stat, index) => (
                    <Grid size={{ xs: 6, sm: 4 }} key={index}>
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={inView ? { scale: 1 } : {}}
                        transition={{ duration: 0.5, delay: index * 0.1 }}
                      >
                        <Box
                          sx={{
                            textAlign: 'center',
                            p: { xs: 1.5, md: 2 },
                            backgroundColor: theme.palette.grey[50],
                            borderRadius: "10px",
                          }}
                        >
                          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', md: '2rem' },  fontWeight: 'bold', color: 'primary.main' }}>
                            {inView && (
                              <CountUp end={stat.value} duration={2.5} suffix={stat.suffix} />
                            )}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'typography.main', fontSize: { xs: '0.8rem', md: '0.875rem' } }}>
                            {stat.label}
                          </Typography>
                        </Box>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </motion.div>
            </Grid>

            <Grid size={{ xs: 12, lg: 6 }}>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <Box sx={{ position: 'relative' }}>
                  <Card sx={{ borderRadius: "15px", overflow: 'hidden', boxShadow: 6 }}>
                    <Box sx={{ position: 'relative' }}>
                      <CardMedia
                        component="video"
                        src={about.videoUrl}
                        alt="Award Ceremony"
                        ref={videoRef}
                        onPlay={handleVideoPlay}
                        onPause={handleVideoPause}
                        onEnded={handleVideoEnded}
                        controls={isVideoPlaying}
                        sx={{
                          cursor: isVideoPlaying ? 'default' : 'pointer',
                        }}
                        onClick={!isVideoPlaying ? handlePlayVideo : undefined}
                      />
                      {!isVideoPlaying && (
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.4)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                          }}
                          onClick={handlePlayVideo}
                        >
                          <motion.div
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                          >
                            <IconButton
                              sx={{
                                width: 80,
                                height: 80,
                                backgroundColor: 'secondary.main',
                                color: 'white',
                                '&:hover': {
                                  backgroundColor: 'secondary.dark',
                                  transform: 'scale(1.1)',
                                },
                                transition: 'all 0.3s ease',
                              }}
                            >
                              <Play size={30} style={{ marginLeft: 4 }} />
                            </IconButton>
                          </motion.div>
                        </Box>
                      )}
                    </Box>
                  </Card>

                  <Grid container spacing={2} sx={{ mt: 4 }}>
                    {about.galleryImages?.map((src, i) => (
                      <Grid size={4} key={i}>
                        <motion.div whileHover={{ scale: 1.05 }}>
                          <Card sx={{ borderRadius: "7px", overflow: 'hidden', cursor: 'pointer' }}>
                            <CardMedia
                              component="img"
                              height="110px"
                              image={galleryImageMap[src] || src}
                              alt={`Gallery ${i + 1}`}
                            />
                          </Card>
                        </motion.div>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>
    );
  };

  // Award Categories Section
  const AwardCategoriesSection = () => {
    // Icon mapping
    const iconMap = {
      Users,
      TrendingUp,
      Lightbulb
    };

    const categoryGroups = [
      {
        title: t('awardCategories.categoryGroups.farmer.title'),
        description: t('awardCategories.categoryGroups.farmer.description'),
        icon: iconMap['Users'],
        gradient: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
        awardsLength: 10
      },
      {
        title: t('awardCategories.categoryGroups.fpo.title'),
        description: t('awardCategories.categoryGroups.fpo.description'),
        icon: iconMap['TrendingUp'],
        gradient: 'linear-gradient(135deg, #2B2B2B 0%, #1A1A1A 100%)',
        awardsLength: 5
      },
      {
        title: t('awardCategories.categoryGroups.innovators.title'),
        description: t('awardCategories.categoryGroups.innovators.description'),
        icon: iconMap['Lightbulb'],
        gradient: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
        awardsLength: 5
      }
    ];

    return (
      <Box sx={{ py: { xs: 8, md: 8 }, backgroundColor: 'white' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                letterSpacing: 1.5,
                fontSize: '0.875rem',
              }}
            >
              {t('awardCategories.overline')}
            </Typography>
            <Typography variant="h2" sx={{
              background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: 900,
              mb: 2
            }}>
              {t('awardCategories.title')}
            </Typography>
            <Typography variant="body1" sx={{ color: 'typography.main', maxWidth: 700, mx: 'auto' }}>
              {t('awardCategories.subtitle')}
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {categoryGroups.map((group, groupIndex) => {
              const Icon = group.icon;
              const isGold = groupIndex === 0 || groupIndex === 2;

              return (
                <Grid size={{ xs: 12, lg: 4 }} key={groupIndex}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: groupIndex * 0.2 }}
                    viewport={{ once: true }}
                    style={{ height: '100%', display: 'flex' }}
                  >
                    <Card
                      sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        overflow: 'hidden',
                        borderRadius: '10px',
                        background: '#FFFFFF',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
                          '& .header-gradient': {
                            transform: 'scale(1.02)',
                          },
                          '& .floating-icon': {
                            transform: 'rotate(10deg) scale(1.1)',
                          }
                        },
                      }}
                    >
                      {/* Header Section with Gradient */}
                      <Box
                        className="header-gradient"
                        sx={{
                          background: group.gradient,
                          p: { xs: 3, lg: 4 },
                          pb: { xs: 3, lg: 5 },
                          minHeight: { xs: 'auto', lg: 380 },
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative',
                          transition: 'transform 0.4s ease',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: '30px',
                            background: 'linear-gradient(to bottom, transparent, rgba(255,255,255,0.3))',
                          }
                        }}
                      >
                        {/* Decorative Pattern */}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            width: { xs: '100px', lg: '200px' },
                            height: { xs: '100px', lg: '200px' },
                            opacity: 0.1,
                            background: `radial-gradient(circle, ${isGold ? '#FFF' : '#D4A747'} 1px, transparent 1px)`,
                            backgroundSize: '20px 20px',
                          }}
                        />

                        {/* Icon Container */}
                        <Box
                          className="floating-icon"
                          sx={{
                            width: { xs: 56, lg: 72 },
                            height: { xs: 56, lg: 72 },
                            borderRadius: '20px',
                            background: isGold ? 'rgba(255,255,255,0.2)' : 'rgba(212,167,71,0.2)',
                            backdropFilter: 'blur(10px)',
                            border: `2px solid ${isGold ? 'rgba(255,255,255,0.3)' : 'rgba(212,167,71,0.3)'}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: { xs: 2, lg: 3 },
                            transition: 'transform 0.4s ease',
                          }}
                        >
                          <Icon size={36} color={isGold ? '#FFFFFF' : '#D4A747'} strokeWidth={2} />
                        </Box>

                        {/* Title & Description */}
                        <Typography
                          variant="h3"
                          sx={{
                            color: isGold ? '#FFFFFF' : '#D4A747',
                            fontWeight: 800,
                            mb: { xs: 1.5, lg: 2 },
                            fontSize: { xs: '1.5rem', lg: '2rem' },
                          }}
                        >
                          {group.title}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: isGold ? 'rgba(255,255,255,0.9)' : 'rgba(212,167,71,0.9)',
                            lineHeight: 1.6,
                            fontSize: { xs: '0.875rem', lg: '0.95rem' },
                          }}
                        >
                          {group.description}
                        </Typography>
                      </Box>

                      {/* Awards List */}
                      <CardContent sx={{ p: { xs: 3, lg: 4 }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ mb: { xs: 2, lg: 3 } }}>
                          <Typography
                            variant="h4"
                            sx={{
                              color: 'typography.main',
                              fontWeight: 700,
                              fontSize: { xs: '2rem', lg: '2.5rem' },
                              mb: 1
                            }}
                          >
                            {group.awardsLength}
                          </Typography>
                          <Typography
                            variant="overline"
                            sx={{
                              color: 'typography.secondary',
                              fontWeight: 600,
                              letterSpacing: 1.2,
                              fontSize: '0.75rem'
                            }}
                          >
                            {t('common:common.awardCategoriesLabel')}
                          </Typography>
                        </Box>

                        {/* Footer with Learn More Button */}
                        <Box sx={{ mt: 'auto' }}>
                          <Button
                            component={Link}
                            to={`/${currentLang}/nominations#categories`}
                            endIcon={<ArrowRight size={18} />}
                            onClick={() => {
                              logCustomEvent('learn_more_clicked', {
                                category_group: group.title,
                                location: 'award_categories_section'
                              });
                            }}
                            sx={{
                              width: '100%',
                              py: 1.5,
                              borderRadius: '10px',
                              background: isGold
                                ? 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)'
                                : 'linear-gradient(135deg, #2B2B2B 0%, #1A1A1A 100%)',
                              color: '#FFFFFF',
                              fontWeight: 600,
                              fontSize: '0.9rem',
                              textTransform: 'none',
                              border: '1px solid',
                              borderColor: isGold ? 'rgba(212, 167, 71, 0.2)' : 'rgba(43, 43, 43, 0.15)',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                borderColor: 'transparent',
                                transform: 'translateY(-2px)',
                                boxShadow: isGold
                                  ? '0 8px 24px rgba(212, 167, 71, 0.3)'
                                  : '0 8px 24px rgba(0, 0, 0, 0.2)',
                                '& .MuiButton-endIcon': {
                                  transform: 'translateX(4px)',
                                },
                              },
                              '& .MuiButton-endIcon': {
                                transition: 'transform 0.3s ease',
                              },
                            }}
                          >
                            {t('common:common.learnMore')}
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>
    );
  };

  // Why Choose Section
  const WhyChooseSection = () => {
    // Icon mapping
    const iconMap = {
      Trophy,
      Users,
      TrendingUp,
      Lightbulb,
      Award,
      Star
    };

    const reasons = [
      { title: t('whyChoose.reasons.credibility.title'), description: t('whyChoose.reasons.credibility.description'), icon: iconMap['Trophy'] },
      { title: t('whyChoose.reasons.expertJury.title'), description: t('whyChoose.reasons.expertJury.description'), icon: iconMap['Users'] },
      { title: t('whyChoose.reasons.growth.title'), description: t('whyChoose.reasons.growth.description'), icon: iconMap['TrendingUp'] },
      { title: t('whyChoose.reasons.innovation.title'), description: t('whyChoose.reasons.innovation.description'), icon: iconMap['Lightbulb'] },
      { title: t('whyChoose.reasons.mediaCoverage.title'), description: t('whyChoose.reasons.mediaCoverage.description'), icon: iconMap['Award'] },
      { title: t('whyChoose.reasons.networking.title'), description: t('whyChoose.reasons.networking.description'), icon: iconMap['Star'] }
    ];

    return (
      <Box sx={{ py: { xs: 8, md: 6}, backgroundColor: '#FFFFFF' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                letterSpacing: 1.5,
                fontSize: '0.875rem',
              }}
            >
              {t('whyChoose.overline')}
            </Typography>
            <Typography variant="h2" sx={{ background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {t('whyChoose.title')}
            </Typography>
            <Typography variant="body1" sx={{ color: 'typography.main', mt: 2, maxWidth: 600, mx: 'auto' }}>
              {t('whyChoose.subtitle')}
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {reasons.map((reason, index) => {
              const Icon = reason.icon;
              return (
                <Grid size={{ xs: 12, md: 6, lg: 4 }} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    whileHover={{ y: -10 }}
                  >
                    <Card
                      sx={{
                        p: 3,
                        height: 320,
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
                        borderRadius: "10px",
                        backgroundColor: '#FFFFFF',
                        '&:hover': {
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <CardContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Box
                          sx={{
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            mb: 2,
                          }}
                        >
                          <Icon size={28} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                          {reason.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'typography.main', flex: 1 }}>
                          {reason.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>
    );
  };

  // Jury Composition Section
  const JuryCompositionSection = () => {
    // Icon mapping
    const iconMap = {
      Award,
      Briefcase,
      Users,
      Heart,
      FileText,
      TrendingUp
    };

    const juryCategories = [
      { title: t('juryComposition.juryCategories.academics.title'), description: t('juryComposition.juryCategories.academics.description'), icon: iconMap['Award'] },
      { title: t('juryComposition.juryCategories.industry.title'), description: t('juryComposition.juryCategories.industry.description'), icon: iconMap['Briefcase'] },
      { title: t('juryComposition.juryCategories.social.title'), description: t('juryComposition.juryCategories.social.description'), icon: iconMap['Heart'] }
    ];

    return (
      <Box sx={{ py: { xs: 8, md: 10 }, backgroundColor: '#FAFAFA' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="overline"
                sx={{
                  color: 'primary.main',
                  fontWeight: 600,
                  letterSpacing: 1.5,
                  fontSize: '0.875rem',
                }}
              >
                {t('juryComposition.overline')}
              </Typography>
              <Typography variant="h2" sx={{
                background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                fontWeight: 900,
                mb: 2
              }}>
                {t('juryComposition.title')}
              </Typography>
              <Typography variant="body1" sx={{ color: 'typography.main', maxWidth: 700, mx: 'auto' }}>
                {t('juryComposition.subtitle')}
              </Typography>
            </Box>

            <Grid container spacing={4} sx={{ alignItems: 'stretch' }}>
              {juryCategories.map((category, index) => {
                const Icon = category.icon;
                const isGold = index % 2 === 0;

                return (
                  <Grid size={{ xs: 12, lg: 4 }} key={index}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      style={{ height: '100%' }}
                    >
                      <Box
                        sx={{
                          height: '100%',
                          minHeight: 280,
                          mb: 2,
                          position: 'relative',
                          overflow: 'visible',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        {/* Main Card */}
                        <Box
                          sx={{
                            height: '100%',
                            background: '#FFFFFF',
                            borderRadius: '15px',
                            p: 4,
                            position: 'relative',
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                            border: '1px solid',
                            borderColor: isGold ? 'rgba(212, 167, 71, 0.12)' : 'rgba(43, 43, 43, 0.08)',
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          {/* Floating Icon Badge */}
                          <Box
                            sx={{
                              position: 'absolute',
                              top: -24,
                              right: 24,
                              width: 50,
                              height: 50,
                              borderRadius: '50%',
                              background: isGold
                                ? 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)'
                                : 'linear-gradient(135deg, #2B2B2B 0%, #1A1A1A 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                              zIndex: 2,
                            }}
                          >
                            <Icon size={20} color={isGold ? '#1A1A1A' : '#FFFFFF'} strokeWidth={2.5} />
                          </Box>

                          {/* Vertical Accent Line */}
                          <Box
                            sx={{
                              position: 'absolute',
                              left: 0,
                              top: '20%',
                              bottom: '20%',
                              width: '4px',
                              background: isGold
                                ? 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)'
                                : 'linear-gradient(135deg, #2B2B2B 0%, #1A1A1A 100%)',
                              borderRadius: '0 4px 4px 0',
                            }}
                          />

                          {/* Content Container */}
                          <Box sx={{ pl: 2, pt: 2, display: 'flex', flexDirection: 'column', flex: 1 }}>
                            {/* Title with Underline */}
                            <Box sx={{ mb: 2.5 }}>
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 700,
                                  color: 'typography.main',
                                  mb: 0.5,
                                  fontSize: '1.2rem',
                                  lineHeight: 1.3,
                                }}
                              >
                                {category.title}
                              </Typography>
                              <Box
                                sx={{
                                  width: '50px',
                                  height: '3px',
                                  background: isGold
                                    ? 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)'
                                    : 'linear-gradient(135deg, #2B2B2B 0%, #1A1A1A 100%)',
                                  borderRadius: '2px',
                                }}
                              />
                            </Box>

                            {/* Description */}
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'typography.secondary',
                                lineHeight: 1.8,
                                fontSize: '0.95rem',
                                flex: 1,
                              }}
                            >
                              {category.description}
                            </Typography>
                          </Box>

                          {/* Bottom Corner Dot Pattern */}
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: 16,
                              right: 16,
                              display: 'flex',
                              gap: 0.5,
                            }}
                          >
                            {[1, 2, 3].map((dot) => (
                              <Box
                                key={dot}
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: '50%',
                                  background: isGold ? 'rgba(212, 167, 71, 0.3)' : 'rgba(43, 43, 43, 0.2)',
                                }}
                              />
                            ))}
                          </Box>

                          {/* Subtle Background Pattern */}
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              width: '150px',
                              height: '150px',
                              opacity: 0.03,
                              background: `radial-gradient(circle, ${isGold ? '#D4A747' : '#2B2B2B'} 1px, transparent 1px)`,
                              backgroundSize: '12px 12px',
                              pointerEvents: 'none',
                            }}
                          />
                        </Box>
                      </Box>
                    </motion.div>
                  </Grid>
                );
              })}
            </Grid>
          </motion.div>
        </Container>
      </Box>
    );
  };

  // Winners Gallery Section
  const WinnersGallery = () => {
    const [playingVideo, setPlayingVideo] = useState(null);
    const videoRefs = useRef([]);

    const handleVideoPlay = (index) => {
      if (playingVideo !== null && playingVideo !== index) {
        videoRefs.current[playingVideo]?.pause();
      }
      setPlayingVideo(index);
      videoRefs.current[index]?.play();
    };

    const handleVideoPause = () => {
      setPlayingVideo(null);
    };

    const winners = winnersGallery.winners;

    return (
      <Box sx={{ py: { xs: 8, md: 6 }, backgroundColor: '#FFFFFF' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                fontWeight: 600,
                letterSpacing: 1.5,
                fontSize: '0.875rem',
              }}
            >
              {t('winnersGallery.overline')}
            </Typography>
            <Typography variant="h2" sx={{ background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              {t('winnersGallery.title')}
            </Typography>
            <Typography variant="body1" sx={{ color: 'typography.main', mt: 2, maxWidth: 600, mx: 'auto' }}>
              {t('winnersGallery.subtitle')}
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {winners.map((winner, index) => (
              <Grid size={{ xs: 12, md: 6 }} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{ width: '100%' }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      borderRadius: "15px",
                      overflow: 'hidden',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
                      backgroundColor: '#FFFFFF',
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '20px',
                        padding: '2px',
                        background: 'linear-gradient(135deg, rgba(212, 167, 71, 0.5) 0%, rgba(184, 144, 28, 0.3) 50%, rgba(212, 167, 71, 0.5) 100%)',
                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude',
                        opacity: 0,
                        transition: 'opacity 0.4s ease',
                      },
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 16px 48px rgba(212, 167, 71, 0.2)',
                        '&::before': {
                          opacity: 1,
                        },
                        '& .winner-image': {
                          transform: 'scale(1.05)',
                        },
                        '& .video-frame': {
                          borderColor: 'rgba(212, 167, 71, 0.5)',
                        },
                        '& .elegant-divider': {
                          width: '70px',
                        },
                        '& .winner-name': {
                          color: '#D4A747',
                        }
                      },
                    }}
                  >

                    {/* Video Container with Decorative Frame */}
                    <Box sx={{ position: 'relative', overflow: 'hidden', width: '100%', aspectRatio: '16/9' }}>
                      {/* Decorative Frame */}
                      <Box
                        className="video-frame"
                        sx={{
                          position: 'relative',
                          width: '100%',
                          height: '100%',
                          overflow: 'hidden',
                          border: '2px solid rgba(212, 167, 71, 0.2)',
                          transition: 'all 0.4s ease',
                        }}
                      >
                        <CardMedia
                          className="winner-image"
                          component="video"
                          src={winner.videoUrl}
                          alt={winner.name}
                          ref={(el) => (videoRefs.current[index] = el)}
                          controls={playingVideo === index}
                          onPlay={() => handleVideoPlay(index)}
                          onPause={handleVideoPause}
                          onEnded={handleVideoPause}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                            filter: 'brightness(0.98) contrast(1.08)',
                            cursor: playingVideo === index ? 'default' : 'pointer',
                          }}
                        />

                        {/* Play Button Overlay */}
                        {playingVideo !== index && (
                          <Box
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              background: 'radial-gradient(circle at center, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.5) 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              backdropFilter: 'blur(2px)',
                              transition: 'all 0.4s ease',
                              '&:hover': {
                                background: 'radial-gradient(circle at center, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.6) 100%)',
                                backdropFilter: 'blur(3px)',
                                '& .play-ring': {
                                  transform: 'scale(1.2)',
                                  opacity: 0,
                                }
                              }
                            }}
                            onClick={() => handleVideoPlay(index)}
                          >
                            {/* Animated Ring */}
                            <Box
                              className="play-ring"
                              sx={{
                                position: 'absolute',
                                width: 100,
                                height: 100,
                                borderRadius: '50%',
                                border: '3px solid rgba(212, 167, 71, 0.5)',
                                transition: 'all 0.6s ease',
                                opacity: 0.6,
                              }}
                            />

                            <motion.div
                              whileHover={{ scale: 1.15, rotate: 5 }}
                              whileTap={{ scale: 0.9 }}
                            >
                              <Box
                                sx={{
                                  width: 90,
                                  height: 90,
                                  borderRadius: '50%',
                                  background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  boxShadow: '0 12px 48px rgba(212, 167, 71, 0.5), 0 0 0 8px rgba(212, 167, 71, 0.1)',
                                  border: '4px solid rgba(255, 255, 255, 0.95)',
                                  position: 'relative',
                                  transition: 'all 0.3s ease',
                                  '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    inset: -4,
                                    borderRadius: '50%',
                                    background: 'linear-gradient(135deg, rgba(212, 167, 71, 0.3), transparent)',
                                    animation: 'pulse 2s ease-in-out infinite',
                                  },
                                  '@keyframes pulse': {
                                    '0%, 100%': { opacity: 0.3, transform: 'scale(1)' },
                                    '50%': { opacity: 0.6, transform: 'scale(1.1)' },
                                  },
                                  '&:hover': {
                                    boxShadow: '0 16px 64px rgba(212, 167, 71, 0.7), 0 0 0 12px rgba(212, 167, 71, 0.15)',
                                  }
                                }}
                              >
                                <Play size={36} fill="#FFFFFF" color="#FFFFFF" style={{ marginLeft: '4px' }} />
                              </Box>
                            </motion.div>
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* Enhanced Card Content */}
                    <CardContent sx={{
                      p: 3,
                      position: 'relative',
                      flex: '0 0 auto',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                      background: '#FFFFFF',
                    }}>

                      {/* Elegant Divider */}
                      <Box
                        className="elegant-divider"
                        sx={{
                          width: '40px',
                          height: '3px',
                          background: 'linear-gradient(90deg, #D4A747 0%, #B8901C 100%)',
                          borderRadius: '2px',
                          transition: 'all 0.4s ease',
                        }}
                      />

                      {/* Winner Name */}
                      <Typography
                        className="winner-name"
                        variant="h5"
                        sx={{
                          fontWeight: 700,
                          color: '#1A1A1A',
                          lineHeight: 1.3,
                          fontSize: { xs: '1.25rem', sm: '1.4rem' },
                          transition: 'color 0.3s ease',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          wordBreak: 'break-word',
                        }}
                      >
                        {winner.name}
                      </Typography>

                      {/* Organization */}
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#666',
                          fontWeight: 500,
                          fontSize: '0.95rem',
                          lineHeight: 1.5,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          wordBreak: 'break-word',
                          mt: -1
                        }}
                      >
                        {winner.org}
                      </Typography>

                      {/* Enhanced Category Chip */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <Chip
                          label={winner.category}
                          icon={<Award size={14} color="#B8901C" />}
                          sx={{
                            backgroundColor: 'rgba(212, 167, 71, 0.1)',
                            color: '#B8901C',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            borderRadius: '20px',
                            border: '1.5px solid rgba(212, 167, 71, 0.25)',
                            height: '32px',
                            maxWidth: '100%',
                            '& .MuiChip-label': {
                              px: 1.5,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            },
                            '& .MuiChip-icon': {
                              ml: 0.75,
                              mr: -0.5,
                            }
                          }}
                        />
                      </Box>

                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* Enhanced View All Winners Button */}
          <Box sx={{ textAlign: 'center', mt: 8 }}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                component={Link}
                to={`/${currentLang}/winners`}
                variant="contained"
                size="large"
                endIcon={<ArrowRight size={20} />}
                onClick={() => {
                  logCustomEvent('view_all_winners_clicked', {
                    location: 'winners_gallery_section'
                  });
                }}
                sx={{
                  background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '12px',
                  boxShadow: '0 12px 40px rgba(59, 127, 60, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  position: 'relative',
                  overflow: 'hidden',
                  letterSpacing: '0.02em',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #B8901C 0%, #9A7A18 100%)',
                    boxShadow: '0 20px 60px rgba(212, 167, 71, 0.4)',
                    transform: 'translateY(-3px)',
                    '& .button-glow': {
                      opacity: 1,
                    },
                    '& .MuiButton-endIcon': {
                      transform: 'translateX(4px)',
                    },
                  },
                  '& .MuiButton-endIcon': {
                    transition: 'transform 0.3s ease',
                  },
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                    transition: 'left 0.6s ease',
                  },
                  '&:hover::before': {
                    left: '100%',
                  },
                }}
              >
                <Box
                  className="button-glow"
                  sx={{
                    position: 'absolute',
                    inset: '-2px',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 70%)',
                    borderRadius: '12px',
                    opacity: 0,
                    transition: 'opacity 0.4s ease',
                    zIndex: -1,
                  }}
                />
                {t('winnersGallery.viewAllWinners')}
              </Button>
            </motion.div>
          </Box>
        </Container>
      </Box>
    );
  };

  // Path to Awards Section
  const PathToAwards = () => {
    const { ref } = useInView({ threshold: 0.3 });

    // Icon mapping
    const iconMap = {
      UserPlus,
      FileText,
      Search,
      Gavel,
      Microscope,
      PartyPopper
    };

    const steps = [
      {
        title: t('pathToAwards.steps.nominations.title'),
        description: t('pathToAwards.steps.nominations.description'),
        detail: t('pathToAwards.steps.nominations.detail'),
        estimatedTime: t('pathToAwards.steps.nominations.date'),
        icon: iconMap['UserPlus']
      },
      {
        title: t('pathToAwards.steps.deadline.title'),
        description: t('pathToAwards.steps.deadline.description'),
        detail: t('pathToAwards.steps.deadline.detail'),
        estimatedTime: t('pathToAwards.steps.deadline.date'),
        icon: iconMap['FileText']
      },
      {
        title: t('pathToAwards.steps.screening.title'),
        description: t('pathToAwards.steps.screening.description'),
        detail: t('pathToAwards.steps.screening.detail'),
        estimatedTime: t('pathToAwards.steps.screening.date'),
        icon: iconMap['Search']
      },
      {
        title: t('pathToAwards.steps.internal.title'),
        description: t('pathToAwards.steps.internal.description'),
        detail: t('pathToAwards.steps.internal.detail'),
        estimatedTime: t('pathToAwards.steps.internal.date'),
        icon: iconMap['Gavel']
      },
      {
        title: t('pathToAwards.steps.preparation.title'),
        description: t('pathToAwards.steps.preparation.description'),
        detail: t('pathToAwards.steps.preparation.detail'),
        estimatedTime: t('pathToAwards.steps.preparation.date'),
        icon: iconMap['Microscope']
      },
      {
        title: t('pathToAwards.steps.ceremony.title'),
        description: t('pathToAwards.steps.ceremony.description'),
        detail: t('pathToAwards.steps.ceremony.detail'),
        estimatedTime: t('pathToAwards.steps.ceremony.date'),
        icon: iconMap['PartyPopper']
      }
    ];

    return (
      <Box
        ref={ref}
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(135deg, #D4A747 0%, #B8901C 100%)',
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `
              radial-gradient(circle at 25% 25%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.05) 0%, transparent 50%)
            `,
          }}
        />
        
        {/* Floating Elements */}
        <Box
          sx={{
            position: 'absolute',
            width: '200px',
            height: '200px',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255, 255, 255, 0.05) 0%, transparent 70%)',
            top: '10%',
            right: '10%',
            animation: 'float 6s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0px)' },
              '50%': { transform: 'translateY(-20px)' },
            },
          }}
        />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Box sx={{ textAlign: 'center', mb: 8 }}>
              <Typography
                variant="overline"
                sx={{
                  color: 'secondary.light',
                  fontWeight: 600,
                  letterSpacing: 1.5,
                  fontSize: '0.875rem',
                }}
              >
                {t('pathToAwards.overline')}
              </Typography>

              <Typography
                variant="h2"
                sx={{
                  background: 'linear-gradient(135deg, #FFFFFF 0%, #F4E4BC 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontWeight: 900,
                  mb: 2
                }}
              >
                {t('pathToAwards.title')}
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  mt: 2,
                  maxWidth: 600,
                  mx: 'auto',
                  opacity: 0.9,
                  lineHeight: 1.6
                }}
              >
                {t('pathToAwards.subtitle')}
              </Typography>
            </Box>
          </motion.div>

          {/* Timeline Container */}
          <Box sx={{ position: 'relative', maxWidth: 1200, mx: 'auto' }}>
            {/* Progress Line */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: 0,
                right: 0,
                height: 4,
                background: 'rgba(255, 255, 255, 0.2)',
                borderRadius: 2,
                transform: 'translateY(-50%)',
                zIndex: 1,
                display: { xs: 'none', md: 'block' }
              }}
            />

            <Grid container spacing={{ xs: 3, md: 2 }} sx={{ position: 'relative', zIndex: 3, alignItems: 'stretch' }}>
              {steps.map((step, index) => {
                const Icon = step.icon;

                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }} key={index}>
                    <motion.div
                      initial={{ opacity: 0, y: 50 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      style={{ height: '100%' }}
                    >
                      <Box
                        sx={{
                          position: 'relative',
                          textAlign: 'center',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        {/* Step Circle */}
                        <Box
                          sx={{
                            position: 'relative',
                            width: 70,
                            height: 80,
                            mx: 'auto',
                            mb: 3,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #FFFFFF 0%, #F4E4BC 100%)',
                            border: '3px solid #FFFFFF',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 20px 40px rgba(255, 255, 255, 0.3)',
                          }}
                        >
                          <Icon 
                            size={28} 
                            color="#D4A747"
                          />
                        </Box>

                        {/* Content Card */}
                        <Box
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.15)',
                            backdropFilter: 'blur(12px)',
                            borderRadius: 3,
                            p: 3,
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            minHeight: 360,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                          }}
                        >
                          {/* Date Chip at Top */}
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              backgroundColor: 'rgba(212, 167, 71, 0.8)',
                              borderRadius: 2,
                              py: 0.5,
                              px: 1.5,
                              mb: 2,
                              border: '1px solid #F4E4BC',
                              height: '28px',
                            }}
                          >
                            <Calendar size={12} style={{ color: '#F4E4BC' }} />
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                whiteSpace: 'nowrap',
                                color: '#F4E4BC'
                              }}
                            >
                              {step.estimatedTime}
                            </Typography>
                          </Box>

                          {/* Title */}
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              mb: 1.5,
                              fontSize: '1rem',
                              lineHeight: 1.4,
                              textAlign: 'center',
                            }}
                          >
                            {step.title}
                          </Typography>

                          {/* Description */}
                          <Typography
                            variant="body2"
                            sx={{
                              opacity: 0.9,
                              mb: 2,
                              lineHeight: 1.6,
                              fontSize: '0.875rem',
                              textAlign: 'center',
                            }}
                          >
                            {step.description}
                          </Typography>

                          {/* Detail Content */}
                          <Typography
                            variant="caption"
                            sx={{
                              opacity: 0.8,
                              display: 'block',
                              lineHeight: 1.7,
                              fontSize: '0.8rem',
                              textAlign: 'center',
                              flex: 1,
                            }}
                          >
                            {step.detail}
                          </Typography>
                        </Box>
                      </Box>
                    </motion.div>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Container>
      </Box>
    );
  };

  return (
    <Box sx={{ overflowX: 'hidden', width: '100%' }}>
      <Helmet>
        <title>{t('meta.title')}</title>
        <meta name="description" content={t('meta.description')} />
      </Helmet>
      <HeroSection />
      <AboutSection />
      <SponsorsSection />
      <AwardCategoriesSection />
      <WhyChooseSection />
      <JuryCompositionSection />
      <WinnersGallery />
      <PathToAwards />
    </Box>
  );
};

export default Home;